import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft } from "react-icons/fa";
import { API_URL } from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#4F46E5" },
    secondary: { main: "#10B981" },
    background: { default: "#F5F7FF", paper: "#FFFFFF" },
    text: { primary: "#1E1B4B", secondary: "#6B7280" },
  },
  typography: {
    fontFamily: '"DM Sans", "Helvetica Neue", sans-serif',
  },
  shape: { borderRadius: 16 },
});

/* ── animation variants ── */
const pageVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const AdminLocationWithStudentCountPage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    fetchLocationsWithStudentCounts();
  }, []);

  const fetchLocationsWithStudentCounts = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/locations-with-student-counts/`
      );
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const totalStudents = locations.reduce(
    (sum, l) => sum + (l.student_count || 0),
    0
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
         width:"100%",
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #F5F7FF 0%, #EEF2FF 50%, #F0FDF4 100%)",
          p: { xs: 2, sm: 4 },
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1152px", // Exact max-w-6xl
            mx: "auto", // mx-auto
          }}
        >
          <motion.div
            variants={pageVariants}
            initial="hidden"
            animate="visible"
            sx={{}}
          >
            {/* ── top bar ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 4,
              }}
            >
              <motion.div whileHover={{ x: -4 }} whileTap={{ scale: 0.92 }}>
                <Button
                  startIcon={<FaArrowLeft size={13} />}
                  onClick={() => navigate(-1)}
                  sx={{
                    color: "#4F46E5",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderRadius: 3,
                    px: 2,
                    py: 1,
                    background: "rgba(79,70,229,0.08)",
                    "&:hover": { background: "rgba(79,70,229,0.15)" },
                  }}
                >
                  Back
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.4 }}
              >
                <Button
                  onClick={fetchLocationsWithStudentCounts}
                  disabled={refreshing}
                  sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(79,70,229,0.08)",
                    color: "#4F46E5",
                    "&:hover": { background: "rgba(79,70,229,0.18)" },
                  }}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </Button>
              </motion.div>
            </Box>

            {/* ── header card ── */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                color: "#fff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* decorative blobs */}
              <Box
                sx={{
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  right: 60,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.06)",
                }}
              />

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
              >
                <LocationOnIcon sx={{ fontSize: 22 }} />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  letterSpacing="-0.3px"
                >
                  Student Locations
                </Typography>
              </Box>
              <Typography sx={{ opacity: 0.8, fontSize: "0.85rem", mb: 2 }}>
                Overview of all registered locations
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Chip
                  label={`${locations.length} Locations`}
                  sx={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                />
                <Chip
                  icon={
                    <GroupsIcon
                      sx={{ color: "#fff !important", fontSize: 16 }}
                    />
                  }
                  label={`${totalStudents} Students`}
                  sx={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                />
              </Box>
            </Paper>

            {/* ── nav button ── */}
            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
              <Paper
                elevation={0}
                onClick={() => navigate("/admin-location-country")}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 3,
                  border: "1.5px solid #E0E7FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  background: "#fff",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(79,70,229,0.12)",
                    borderColor: "#4F46E5",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: "#EEF2FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LocationOnIcon sx={{ color: "#4F46E5", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      fontWeight={700}
                      fontSize="0.9rem"
                      color="#1E1B4B"
                    >
                      Manage Locations
                    </Typography>
                    <Typography fontSize="0.75rem" color="#6B7280">
                      Add, edit or remove locations
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon sx={{ color: "#4F46E5", fontSize: 14 }} />
              </Paper>
            </motion.div>

            {/* ── list ── */}
            <AnimatePresence mode="wait">
              {refreshing ? (
                <motion.div
                  key="loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      py: 6,
                    }}
                  >
                    <CircularProgress
                      sx={{ color: "#4F46E5" }}
                      size={36}
                      thickness={4}
                    />
                    <Typography color="#6B7280" fontSize="0.85rem">
                      Fetching locations…
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  variants={listVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: "1.5px solid #E0E7FF",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {locations.length === 0 ? (
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <Typography color="#6B7280" fontSize="0.9rem">
                          No locations found.
                        </Typography>
                      </Box>
                    ) : (
                      locations.map((item, index) => (
                        <React.Fragment key={item.id}>
                          <motion.div
                            variants={itemVariants}
                            onHoverStart={() => setHoveredId(item.id)}
                            onHoverEnd={() => setHoveredId(null)}
                          >
                            <Box
                              sx={{
                                px: 3,
                                py: 2.2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                background:
                                  hoveredId === item.id
                                    ? "#F5F7FF"
                                    : "transparent",
                                transition: "background 0.2s",
                                cursor: "default",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <motion.div
                                  animate={{
                                    scale: hoveredId === item.id ? 1.15 : 1,
                                  }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <Box
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: 2,
                                      background: "#EEF2FF",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <LocationOnIcon
                                      sx={{ color: "#4F46E5", fontSize: 16 }}
                                    />
                                  </Box>
                                </motion.div>
                                <Box>
                                  <Typography
                                    fontWeight={600}
                                    fontSize="0.9rem"
                                    color="#1E1B4B"
                                  >
                                    {item.state}
                                  </Typography>
                                  <Typography
                                    fontSize="0.75rem"
                                    color="#6B7280"
                                  >
                                    {item.country_name}
                                  </Typography>
                                </Box>
                              </Box>

                              <motion.div
                                animate={{
                                  scale: hoveredId === item.id ? 1.05 : 1,
                                }}
                              >
                                <Chip
                                  icon={
                                    <GroupsIcon
                                      sx={{
                                        fontSize: "14px !important",
                                        color: "#10B981 !important",
                                      }}
                                    />
                                  }
                                  label={`${item.student_count} students`}
                                  size="small"
                                  sx={{
                                    background: "#F0FDF4",
                                    color: "#059669",
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    border: "1px solid #A7F3D0",
                                  }}
                                />
                              </motion.div>
                            </Box>
                          </motion.div>
                          {index < locations.length - 1 && (
                            <Divider sx={{ mx: 3, borderColor: "#F3F4F6" }} />
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </Paper>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminLocationWithStudentCountPage;
